import React, { useMemo, useState } from "react"

import { ChevronDown } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { LogsFilters, usePeopleActivityLogs } from "@/hooks/usePeopleActivityLogs"
import DashboardLayout from "@/layouts/DashboardLayout"

const DEFAULT_LIMIT = 25

const PeopleActivityLogs: React.FC = () => {
  const [filters, setFilters] = useState<LogsFilters>({
    email: "",
    entity: "people",
    entityId: "",
    action: "any",
    success: "any",
    from: null,
    to: null,
    page: 1,
    limit: DEFAULT_LIMIT,
  })

  const { data, isLoading, isError, refetch } = usePeopleActivityLogs(filters)
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({})

  const totalPages = useMemo(() => {
    if (!data) return 1
    return Math.max(1, Math.ceil((data.total || 0) / (filters.limit || DEFAULT_LIMIT)))
  }, [data, filters.limit])

  const applyFilters = () => {
    setFilters((prev) => ({ ...prev, page: 1 }))
    refetch()
  }

  // Inline visual diff component
  const DiffView: React.FC<{
    before: Record<string, unknown> | null
    after: Record<string, unknown> | null
  }> = ({ before, after }) => {
    const keys = Array.from(
      new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]),
    ).sort()

    const fmt = (v: unknown) => {
      try {
        if (typeof v === "string") return v
        return JSON.stringify(v, null, 2)
      } catch {
        return String(v)
      }
    }

    if (keys.length === 0) {
      return <p className="text-xs text-muted-foreground">No field-level changes captured.</p>
    }

    return (
      <div className="border rounded">
        <div className="grid grid-cols-12 text-xs font-semibold text-muted-foreground bg-muted/50 px-3 py-2 rounded-t">
          <div className="col-span-3">Field</div>
          <div className="col-span-4">Before</div>
          <div className="col-span-5">After</div>
        </div>
        <div className="divide-y">
          {keys.map((k) => (
            <div key={k} className="grid grid-cols-12 gap-2 px-3 py-2 items-start">
              <div className="col-span-3 font-mono text-xs break-all">{k}</div>
              <div className="col-span-4">
                <pre className="text-xs bg-red-50 text-red-900 border border-red-100 p-2 rounded max-h-48 overflow-auto whitespace-pre-wrap break-words">
                  {fmt((before ?? {})[k])}
                </pre>
              </div>
              <div className="col-span-5">
                <pre className="text-xs bg-green-50 text-green-900 border border-green-100 p-2 rounded max-h-48 overflow-auto whitespace-pre-wrap break-words">
                  {fmt((after ?? {})[k])}
                </pre>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const clearFilters = () => {
    setFilters({
      email: "",
      entity: "",
      entityId: "",
      action: "any",
      success: "any",
      from: null,
      to: null,
      page: 1,
      limit: DEFAULT_LIMIT,
    })
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-secondary">Activity Logs</h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Actor Email</p>
                <Input
                  placeholder="email@domain.com"
                  value={filters.email || ""}
                  onChange={(e) => setFilters((f) => ({ ...f, email: e.target.value }))}
                />
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Entity</p>
                <Input
                  placeholder="people | applications | ..."
                  value={filters.entity || ""}
                  onChange={(e) => setFilters((f) => ({ ...f, entity: e.target.value }))}
                />
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Action</p>
                <Select
                  value={(filters.action as string) || "any"}
                  onValueChange={(val) =>
                    setFilters((f) => ({
                      ...f,
                      action: val as "any" | "update" | "create" | "delete",
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="update">Update</SelectItem>
                    <SelectItem value="create">Create</SelectItem>
                    <SelectItem value="delete">Delete</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Entity ID</p>
                <Input
                  placeholder="e.g. recXXXXXXXXXXXXXX"
                  value={filters.entityId || ""}
                  onChange={(e) => setFilters((f) => ({ ...f, entityId: e.target.value }))}
                />
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Success</p>
                <Select
                  value={String(filters.success ?? "any")}
                  onValueChange={(val) =>
                    setFilters((f) => ({
                      ...f,
                      success: val === "any" ? "any" : val === "true",
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="true">Success</SelectItem>
                    <SelectItem value="false">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">From (ISO)</p>
                <Input
                  placeholder="2025-09-25T00:00:00Z"
                  value={filters.from || ""}
                  onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
                />
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">To (ISO)</p>
                <Input
                  placeholder="2025-09-26T00:00:00Z"
                  value={filters.to || ""}
                  onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
                />
              </div>
              <div className="flex items-end gap-2">
                <Button onClick={applyFilters}>Apply</Button>
                <Button variant="outline" onClick={clearFilters}>
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Logs</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : isError ? (
              <p className="text-sm text-red-500">Failed to load logs</p>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-12 gap-2 text-sm font-semibold text-muted-foreground">
                  <div className="col-span-3">Timestamp</div>
                  <div className="col-span-3">Actor</div>
                  <div className="col-span-2">Entity</div>
                  <div className="col-span-2">Entity ID</div>
                  <div className="col-span-1">Action</div>
                  <div className="col-span-1">Success</div>
                </div>
                <div className="divide-y">
                  {data?.logs?.map((log) => {
                    const isOpen = !!openRows[log.id]
                    return (
                      <div key={log.id} className="py-1">
                        <button
                          type="button"
                          onClick={() => setOpenRows((m) => ({ ...m, [log.id]: !isOpen }))}
                          className="w-full grid grid-cols-12 gap-2 items-center rounded-md px-2 py-2 text-left hover:bg-muted/50 transition-colors"
                        >
                          <div className="col-span-3">
                            {new Date(log.created_at).toLocaleString()}
                          </div>
                          <div className="col-span-3">
                            <div className="flex items-center gap-2">
                              <span>{log.actor_email || "Unknown"}</span>
                              {log.actor_role && <Badge variant="outline">{log.actor_role}</Badge>}
                            </div>
                          </div>
                          <div className="col-span-2 truncate">{log.entity}</div>
                          <div className="col-span-2 truncate">{log.entity_id || "-"}</div>
                          <div className="col-span-1">
                            <Badge variant="secondary">{log.action}</Badge>
                          </div>
                          <div className="col-span-1 flex items-center justify-between">
                            {log.success ? (
                              <Badge className="bg-green-100 text-green-800 border-green-200">
                                Yes
                              </Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-800 border-red-200">No</Badge>
                            )}
                            <ChevronDown
                              className={`ml-2 h-4 w-4 shrink-0 transition-transform ${isOpen ? "rotate-180" : "rotate-0"}`}
                            />
                          </div>
                        </button>
                        {isOpen && (
                          <div className="mt-3 mb-4 ml-2">
                            <div className="space-y-3">
                              <DiffView before={log.before} after={log.after} />
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Error</p>
                                <pre className="text-xs bg-muted p-2 rounded overflow-auto max-h-60">
                                  {JSON.stringify(log.error, null, 2)}
                                </pre>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                  {(!data || data.logs.length === 0) && (
                    <p className="text-sm text-muted-foreground">No logs found</p>
                  )}
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between pt-2">
                  <div className="text-sm text-muted-foreground">
                    Page {filters.page} of {totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      disabled={filters.page === 1}
                      onClick={() =>
                        setFilters((f) => ({ ...f, page: Math.max(1, (f.page || 1) - 1) }))
                      }
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      disabled={filters.page === totalPages}
                      onClick={() =>
                        setFilters((f) => ({ ...f, page: Math.min(totalPages, (f.page || 1) + 1) }))
                      }
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}

export default PeopleActivityLogs
